import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Country, GeoPoint, I18nModule, UserAddressService } from '@spartacus/core';
import { combineLatest, Subscription, take, tap } from 'rxjs';
import { CustomValidatorService } from '../../../../../../components/form-elements/custom-validator.service';
import { FormErrorsModule } from '@spartacus/storefront';
import { CustomStoreFinderService } from '../../core/custom-store-finder.service';
import { initialStatus, loadingStatus } from '../../../../../../core/models/request-status.model';
import { Option } from '../../../../../../core/models/option.model';
import { PointOfServicesRequestConfig } from '../../core/custom-store-finder.model';
import { GeolocationService } from '../../../../../../core/services/geolocation.service';
import { CountriesDropdownComponent } from '../../../../../../components/form-elements/countries-dropdown/countries-dropdown.component';
import { getLastFromArray } from '../../../../../../core/services/simple-tools';

@Component({
  selector: 'store-finder-form',
  standalone: true,
  imports: [CommonModule, I18nModule, ReactiveFormsModule, FormErrorsModule, CountriesDropdownComponent],
  templateUrl: './store-finder-form.component.html',
  styleUrl: './store-finder-form.component.scss',
})
export class StoreFinderFormComponent implements OnInit, OnDestroy {
  subscription = new Subscription();
  subscriptionFormValues = new Subscription();
  country?: Country = { isocode: 'US' };

  form: FormGroup = this.fb.group({
    country: this.fb.group({
      isocode: [null, this.customValidatorService.required()],
    }),
    search: ['', [this.customValidatorService.required()]],
    distance: ['', []],
    unit: ['', []],
    product: ['', []],
    whereToBuy: ['', [this.customValidatorService.required()]],
  });

  countries$ = this.userAddressService.getDeliveryCountries().pipe(
    tap((countries: Country[]) => {
      if (Object.keys(countries).length === 0) {
        this.userAddressService.loadDeliveryCountries();
      }
    }),
  );

  distances$ = this.customStoreFinderService.distances$;
  units$ = this.customStoreFinderService.units$;
  products$ = this.customStoreFinderService.products$;
  types$ = this.customStoreFinderService.types$;
  geoPoint?: GeoPoint;

  constructor(
    private fb: FormBuilder,
    private customValidatorService: CustomValidatorService,
    private customStoreFinderService: CustomStoreFinderService,
    protected geolocationService: GeolocationService,
    protected userAddressService: UserAddressService,
  ) {}

  ngOnInit(): void {
    this.getGeoLocation();
    this.formInitialState();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.subscriptionFormValues.unsubscribe();
  }

  getGeoLocation() {
    this.subscription.add(
      this.geolocationService.getCurrentPosition().subscribe(position => {
        if (position.coords.latitude && position.coords.longitude) {
          this.setGeoLocation(position);
          this.getCountry(position.coords.latitude, position.coords.longitude);
        }
      }),
    );
  }

  setGeoLocation(position: GeolocationPosition) {
    this.geoPoint = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    };

    this.form.get('search')?.clearValidators();
    this.form.get('search')?.updateValueAndValidity();
  }

  setCountry(country?: Country) {
    const acronym = (country?.isocode || '').toUpperCase();
    this.form.get('country')?.get('isocode')?.setValue(acronym);
    this.country = country;
  }

  createTypeOptions(types: Option[]): void {
    this.form.get('whereToBuy')?.setValue('');

    types.forEach(type => {
      this.form.removeControl(type.code);
    });

    types.forEach(type => {
      this.form.addControl(type.code, this.fb.control(false));
      this.setType(type, type.selected ? 'add' : 'remove');
      this.form.get(type.code)?.setValue(type.selected);
    });
  }

  handlerClickType(input: HTMLInputElement, option: Option) {
    this.setType(option, input.checked ? 'add' : 'remove');
  }

  setType(option: Option, operation: 'add' | 'remove') {
    let types: string[] =
      this.form
        .get('whereToBuy')
        ?.value.split(',')
        .filter((type: string) => type) || [];

    if (operation === 'add') {
      types.push(option.code);
    } else {
      types = types.filter(type => type !== option.code);
    }

    this.form.get('whereToBuy')?.setValue(types.join(','));
  }

  formInitialState() {
    this.form.markAsUntouched();
    this.form.markAsPristine();
    this.customStoreFinderService.pointOfServicesRequest$.next(initialStatus);

    this.subscriptionFormValues.add(
      combineLatest([this.distances$, this.units$, this.products$, this.types$]).subscribe(
        ([distances, units, products, types]) => {
          const getSelectedOption = (options: Option[]) => options.find(option => option.selected);

          this.country = { isocode: 'US' };
          this.setCountry(this.country);
          this.form.get('search')?.setValue('');
          this.form.get('distance')?.setValue(getSelectedOption(distances)?.code || '');
          this.form.get('unit')?.setValue(getSelectedOption(units)?.code || '');
          this.form.get('product')?.setValue(getLastFromArray(products || [])?.code || '');
          this.createTypeOptions(types);

          if (distances.length && units.length && products.length && types.length) {
            this.subscriptionFormValues.unsubscribe();

            if (this.geoPoint?.latitude && this.geoPoint?.longitude) {
              this.submit();
            } else {
              this.requestLocationPermission();
            }
          }
        },
      ),
    );
  }

  requestLocationPermission(): void {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          this.setGeoLocation(position);
          this.submit();
        },
        () => {},
      );
    }
  }

  getCountry(lat: number, lng: number) {
    this.subscription.add(
      this.customStoreFinderService.googleMapIsLoaded$.subscribe((googleIsLoaded: boolean) => {
        if (googleIsLoaded) {
          const geocoder = new google.maps.Geocoder();
          const latLng = new google.maps.LatLng(lat, lng);

          geocoder.geocode({ location: latLng }, (results, status) => {
            if (status === 'OK') {
              if (results && results[0]) {
                const country = results[0].address_components.find(component =>
                  component.types.includes('country'),
                )?.short_name;

                this.country = { isocode: country };
                this.setCountry(this.country);
              }
            }
          });
        }
      }),
    );
  }

  resetForm() {
    this.formInitialState();
  }

  submit() {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      const requestData: PointOfServicesRequestConfig = {
        ...this.form.value,
        countryISOCode: this.country?.name || 'United States',
        types: this.form.get('whereToBuy')?.value,
        latitude: (this.geoPoint?.latitude || '').toString(),
        longitude: (this.geoPoint?.longitude || '').toString(),
      };

      setTimeout(() => {
        this.customStoreFinderService.pointOfServicesRequest$.next(loadingStatus);
      });

      this.customStoreFinderService
        .getPointOfServices(requestData)
        .pipe(take(1))
        .subscribe(response => {
          this.customStoreFinderService.pointOfServicesRequest$.next(response);
        });
    }
  }
}
